import { useState } from "react";
import {
  IconUserCog,
  IconSearch,
  IconAdjustmentsHorizontal,
  IconChevronLeft,
  IconChevronRight,
  IconUserOff,
} from "@tabler/icons-react";
import { ROLE_OPTIONS } from "./data";
import styles from "../../pages/ClientDashboard/TeamManagement/teamManagement.module.css";
import Button from "../UI/Button/button";
import UsersTable from "./UsersTable";
import UsersMobileList from "./UsersMobileList";

export default function UserManagementCard({
  users,
  teams,
  loading,
  filters,
  onFilterChange,
  currentUserId,
  canManageRole,
  onEditUser,
  onRemoveUser,
  onResendInvite,
  pagination,
}) {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const isEmpty = !loading && users.length === 0;
  const paginationLabel = loading
    ? "Loading…"
    : isEmpty
      ? "No results"
      : `Showing 1–${users.length} of ${pagination?.total ?? users.length} user${
          (pagination?.total ?? users.length) !== 1 ? "s" : ""
        }`;

  const canActOn = (user) => canManageRole(user.role);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconUserCog size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>User Management</div>
            <div className={styles.cardSubtitle}>
              {pagination?.total ?? users.length} users in your organisation
            </div>
          </div>
        </div>
      </div>

      <div className={styles.cardBodyNoBottom}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <IconSearch size={16} />
            <input
              type="text"
              placeholder="Search users…"
              value={filters.search}
              onChange={(e) => onFilterChange({ search: e.target.value })}
            />
          </div>

          <select
            className={styles.filterSelect}
            value={filters.role}
            onChange={(e) => onFilterChange({ role: e.target.value })}
          >
            <option value="">All Roles</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select
            className={styles.filterSelect}
            value={filters.teamId}
            onChange={(e) => onFilterChange({ teamId: e.target.value })}
          >
            <option value="">All Teams</option>
            <option value="none">No team</option>
            {teams?.map((t) => (
              <option key={t?._id} value={t?._id}>
                {t?.name}
              </option>
            ))}
          </select>

          {/* Mobile: single filter button replaces the two selects */}
          <Button
            variant="secondary"
            size="sm"
            className={styles.mobileFilterBtn}
            leftIcon={<IconAdjustmentsHorizontal size={15} />}
            onClick={() => setMobileFilterOpen((o) => !o)}
          >
            Filter
          </Button>
        </div>

        {/* Mobile filter panel */}
        {mobileFilterOpen && (
          <div className={styles.mobileFilterPanel}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <select
                className={styles.filterSelect}
                value={filters.role}
                onChange={(e) => onFilterChange({ role: e.target.value })}
              >
                <option value="">All Roles</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <select
                className={styles.filterSelect}
                value={filters.teamId}
                onChange={(e) => onFilterChange({ teamId: e.target.value })}
              >
                <option value="">All Teams</option>
                <option value="none">No team</option>
                {teams?.map((t) => (
                  <option key={t?._id} value={t?._id}>
                    {t?.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {!isEmpty && (
        <>
          <UsersTable
            users={users}
            currentUserId={currentUserId}
            canActOn={canActOn}
            onEditUser={onEditUser}
            onRemoveUser={onRemoveUser}
            onResendInvite={onResendInvite}
          />
          <UsersMobileList
            users={users}
            currentUserId={currentUserId}
            canActOn={canActOn}
            onEditUser={onEditUser}
            onRemoveUser={onRemoveUser}
            onResendInvite={onResendInvite}
          />
        </>
      )}

      {isEmpty && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <IconUserOff size={26} />
          </div>
          <div className={styles.emptyTitle}>No users found</div>
          <div className={styles.emptyText}>
            Try adjusting your search or filter criteria.
          </div>
        </div>
      )}

      <div className={styles.pagination}>
        <span className={styles.paginationInfo}>{paginationLabel}</span>
        <div className={styles.paginationBtns}>
          <button className={styles.pageBtn} disabled>
            <IconChevronLeft size={15} />
          </button>
          <button className={`${styles.pageBtn} ${styles.active}`}>
            {pagination?.page ?? 1}
          </button>
          <button className={styles.pageBtn} disabled>
            <IconChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
